export const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(n || 0);

export const effectiveLineDiscountPct = (row: {
  discount?: number | null;
  not_discountable?: boolean | null;
  r2_net_discount_pct?: number | null;
}): number => {
  const r2Net = Number(row.r2_net_discount_pct);
  if (Number.isFinite(r2Net)) {
    return Math.min(100, Math.max(0, r2Net));
  }
  if (row.not_discountable) return 0;
  const manual = Number(row.discount ?? 0);
  return Number.isFinite(manual) ? Math.min(100, Math.max(0, manual)) : 0;
};

export type KitPricingMode = 'fixed' | 'components';

export const normaliseKitPricingMode = (value: unknown): KitPricingMode => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return normalized === 'components' || normalized === 'component' || normalized === 'component_sum'
    ? 'components'
    : 'fixed';
};

export const shouldPriceItemLine = (row: {
  kit_anchor?: boolean | null;
  kit_component?: boolean | null;
  kit_pricing_mode?: unknown;
}): boolean => {
  const mode = normaliseKitPricingMode(row.kit_pricing_mode);
  if (row.kit_component) return mode === 'components';
  if (row.kit_anchor) return mode !== 'components';
  return true;
};

/**
 * The heading a section subtotal row adds up from: the nearest heading above it with the
 * subtotal's section, else the nearest heading above. R2 contracts repeat a section's
 * block, so the first heading with that section can belong to an earlier block.
 * Returns -1 when no heading is above the subtotal.
 */
export const findSectionSubtotalParentIndex = (
  rows: ReadonlyArray<{ kind?: string | null; section_id?: string | null } | null | undefined>,
  subtotalIndex: number,
): number => {
  const sectionId = rows[subtotalIndex]?.section_id ?? null;
  let nearest = -1;
  for (let index = subtotalIndex - 1; index >= 0; index -= 1) {
    const row = rows[index];
    if (row?.kind !== 'section') continue;
    if (!sectionId || row.section_id === sectionId) return index;
    if (nearest < 0) nearest = index;
  }
  return nearest;
};

export const unwrap = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value ?? null;
};
